import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type InternVendorStatus = "active" | "inactive" | "pending";

export interface InternVendorType {
  _id: string;
  companyName: string; // e.g. "TechCorp Solutions"
  contactPerson: string; // e.g. "John Parker"
  email: string; // e.g. "john@techcorp.com"
  phone: string; // e.g. "+1-234-567-1000"
  website: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  duration: number; // in months
  stipendRange: string;
  availablePositions: number; // e.g. 25
  companyDescription: string;
  internshipRequirements: string;
  status: InternVendorStatus;
  createdAt: string;
  updatedAt: string;
}

export interface InternVendorCreateType {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  website: string;
  industry: string[];
  city: string;
  state: string;
  preferredDepartments: string[];
  duration: number;
  stipendRange: string;
  availablePositions: number;
  companyDescription: string;
  internshipRequirements: string;
  status: InternVendorStatus;
}

export interface InternVendorMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type InternVendorParamsType = {
  search?: string;
  status?: InternVendorStatus | "all";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_INTERN_VENDORS: InternVendorType[] = [
  {
    _id: "1",
    companyName: "TechCorp Solutions",
    contactPerson: "John Parker",
    email: "john@techcorp.com",
    phone: "+1-234-567-1000",
    website: "https://techcorp.com",
    industry: ["Software", "IT Services"],
    city: "San Francisco",
    state: "CA",
    preferredDepartments: ["1", "2"], // Assuming dept IDs
    duration: 6,
    stipendRange: "30000-50000",
    availablePositions: 25,
    companyDescription: "Leading tech solutions provider.",
    internshipRequirements: "Knowledge of React and Node.js",
    status: "active",
    createdAt: "2024-01-01T00:00:00.000Z",
    updatedAt: "2024-01-01T00:00:00.000Z",
  },
  {
    _id: "2",
    companyName: "InnovateLabs",
    contactPerson: "Sarah Johnson",
    email: "sarah@innovatelabs.com",
    phone: "+1-234-567-1001",
    website: "https://innovatelabs.com",
    industry: ["Research", "AI"],
    city: "New York",
    state: "NY",
    preferredDepartments: ["1"],
    duration: 3,
    stipendRange: "20000-40000",
    availablePositions: 18,
    companyDescription: "Innovative AI research lab.",
    internshipRequirements: "Python and ML basics",
    status: "active",
    createdAt: "2024-01-02T00:00:00.000Z",
    updatedAt: "2024-01-02T00:00:00.000Z",
  },
  {
    _id: "3",
    companyName: "StartupHub Inc",
    contactPerson: "Mike Davis",
    email: "mike@startuphub.com",
    phone: "+1-234-567-1002",
    website: "https://startuphub.com",
    industry: ["Incubator", "Management"],
    city: "Austin",
    state: "TX",
    preferredDepartments: ["3", "4"],
    duration: 4,
    stipendRange: "15000-25000",
    availablePositions: 12,
    companyDescription: "Supporting startups to grow.",
    internshipRequirements: "Marketing and Management skills",
    status: "pending",
    createdAt: "2024-01-03T00:00:00.000Z",
    updatedAt: "2024-01-03T00:00:00.000Z",
  },
  {
    _id: "4",
    companyName: "DataSoft Systems",
    contactPerson: "Emily Chen",
    email: "emily@datasoft.com",
    phone: "+1-234-567-1003",
    website: "https://datasoft.com",
    industry: ["Software", "Data Analysis"],
    city: "Seattle",
    state: "WA",
    preferredDepartments: ["1", "2"],
    duration: 5,
    stipendRange: "35000-55000",
    availablePositions: 8,
    companyDescription: "Data analytics solutions.",
    internshipRequirements: "SQL and PowerBI",
    status: "active",
    createdAt: "2024-01-04T00:00:00.000Z",
    updatedAt: "2024-01-04T00:00:00.000Z",
  },
  {
    _id: "5",
    companyName: "CloudBase Corp",
    contactPerson: "Robert Kim",
    email: "robert@cloudbase.com",
    phone: "+1-234-567-1004",
    website: "https://cloudbase.com",
    industry: ["Cloud Computing"],
    city: "Chicago",
    state: "IL",
    preferredDepartments: ["2"],
    duration: 6,
    stipendRange: "40000-60000",
    availablePositions: 0,
    companyDescription: "Cloud infrastructure services.",
    internshipRequirements: "AWS or Azure knowledge",
    status: "inactive",
    createdAt: "2024-01-05T00:00:00.000Z",
    updatedAt: "2024-01-05T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  vendors: InternVendorType[],
  filters: InternVendorParamsType
): { data: InternVendorType[]; total: number } => {
  let result = [...vendors];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (v) =>
        v.companyName.toLowerCase().includes(q) ||
        v.contactPerson.toLowerCase().includes(q) ||
        v.email.toLowerCase().includes(q) ||
        v.city.toLowerCase().includes(q) ||
        v.state.toLowerCase().includes(q) ||
        v.phone.includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((v) => v.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllInternVendors = async (
  filters: InternVendorParamsType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/vendors/intern/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_INTERN_VENDORS, filters);
  return {
    vendors: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as InternVendorMetaType,
  };
};

export const fetchInternVendorById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/vendors/intern/${_id}`);
  const vendor = DUMMY_INTERN_VENDORS.find((v) => v._id === _id);
  if (!vendor) throw new Error("Internship vendor not found");
  return vendor;
};

export const createInternVendor = async (data: InternVendorCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/vendors/intern/", data);
  const newVendor: InternVendorType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_INTERN_VENDORS.push(newVendor);
  return newVendor;
};

export const updateInternVendor = async (
  _id: string,
  data: InternVendorCreateType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/vendors/intern/${_id}`, data);
  const index = DUMMY_INTERN_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Internship vendor not found");
  DUMMY_INTERN_VENDORS[index] = {
    ...DUMMY_INTERN_VENDORS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_INTERN_VENDORS[index];
};

export const deleteInternVendor = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/vendors/intern/${_id}`);
  const index = DUMMY_INTERN_VENDORS.findIndex((v) => v._id === _id);
  if (index === -1) throw new Error("Internship vendor not found");
  const [deleted] = DUMMY_INTERN_VENDORS.splice(index, 1);
  return deleted;
};

// Mock Industry Options Fetcher
export const fetchIndustryOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const INDUSTRIES = [
    { label: "Software Development", value: "Software Development" },
    { label: "Data Science", value: "Data Science" },
    { label: "Marketing", value: "Marketing" },
    { label: "Finance", value: "Finance" },
    { label: "Healthcare", value: "Healthcare" },
    { label: "Education", value: "Education" },
    { label: "Manufacturing", value: "Manufacturing" },
    { label: "Consulting", value: "Consulting" },
  ];

  const filtered = INDUSTRIES.filter((i) =>
    i.label.toLowerCase().includes((params.search || "").toLowerCase())
  );

  return {
    items: filtered,
    hasMore: false,
    total: filtered.length,
  };
};

export const fetchInternVendorOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  const search = (params.search || "").toLowerCase();
  const filtered = DUMMY_INTERN_VENDORS.filter((v) =>
    v.companyName.toLowerCase().includes(search)
  );
  return {
    items: filtered.map((v) => ({
      label: v.companyName,
      value: v._id,
    })),
    hasMore: false,
    total: filtered.length,
  };
};

export const internVendorService = {
  fetchAllInternVendors,
  fetchInternVendorById,
  createInternVendor,
  updateInternVendor,
  deleteInternVendor,
  fetchIndustryOptions,
  fetchInternVendorOptions,
};
